import React, { Suspense } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { TXProvider } from "@transifex/react";
import { RootLayout } from "layouts/root-layout";
import { AccountLayout } from "layouts/account-layout";
import { preserveQueryParams } from "helpers";
import { PatientAuthProvider } from "context/patient-auth";
import { KioskAuthProvider } from "context/kiosk-auth";
import { AdminContentProvider } from "context/admin-content";
import { LocationProvider } from 'context/location';
import { PreferredLanguageProvider } from "context/preferred-language";
import { PatientRoute } from "./patient-route";
import { Loading } from "components/loading";

import PatientLanding from "pages/patient-landing";
import KioskLanding from "pages/kiosk-landing";
import Passwordless from "pages/passwordless";
import PasswordlessVerify from "pages/passwordless-verify";
import { ResendCodeEmail } from "pages/resend-code-email";
import EmailPassword from "pages/email-password";
import PasswordlessConfirm from "pages/passwordless-code";
import ForgotPassword from "pages/forgot-password";
import ForgotPasswordConfirm from "pages/forgot-password-confirm";
import MatchPatient from "pages/match-patient";
import { Settings } from "pages/settings";
import { PersonalInformation } from "pages/settings/personal-information";
import { ContactInformation } from "pages/settings/contact-information";
import { Consent } from "pages/settings/consent";
import PatientResults from "pages/patient-results";
import { Education } from "pages/education";
import { RiskReportsList } from "pages/risk-reports-list";
import { RiskReport } from "pages/risk-report";
import { TakeAction } from "pages/take-action";
import { PatientHub } from "pages/kiosk-landing/kiosk-results-page/patient-hub";
import { Pendo } from "components/pendo";
import { Zendesk } from "components/zendesk";
import { Toaster } from "storybook/components/toast";
import { LanguagePrompt } from "components/language-prompt";

const KioskRoutes = () => (
  <KioskAuthProvider>
    <AdminContentProvider>
      <>
        <Switch>
          <Route path="/institution" exact>
            <KioskLanding />
          </Route>
          <Route path="/institution/patient-hub" exact>
            <PatientHub />
          </Route>
        </Switch>
        <LocationProvider>
          <Pendo />
        </LocationProvider>
      </>
    </AdminContentProvider>
  </KioskAuthProvider>
);

const PatientRoutes = () => (
  <PatientAuthProvider>
    <AdminContentProvider>
      <>
        <Toaster theme="default" />
        <Switch>
          <Route
            path="/login"
            render={() => (
              <AccountLayout>
                <Switch>
                  <Route
                    exact
                    path="/login/passwordless"
                    component={Passwordless}
                  />
                  <Route
                    exact
                    path="/login/verify"
                    component={PasswordlessVerify}
                  />
                  <Route
                    exact
                    path="/login/passwordless/confirm"
                    component={PasswordlessConfirm}
                  />
                  <Route
                    exact
                    path="/login/password"
                    component={EmailPassword}
                  />
                  <Route exact path="/login/code" component={ResendCodeEmail} />
                  <Route exact path="/login/info" component={MatchPatient} />
                  <Route
                    exact
                    path="/login/forgot"
                    component={ForgotPassword}
                  />
                  <Route
                    exact
                    path="/login/forgot/confirm"
                    component={ForgotPasswordConfirm}
                  />
                  <Redirect to="/login/passwordless" />
                </Switch>
              </AccountLayout>
            )}
          />
          <PreferredLanguageProvider>
            <>
              <LanguagePrompt />
              <Switch>
                <PatientRoute path="/" exact component={PatientLanding} />
                <PatientRoute path="/settings" exact component={Settings} />
                <PatientRoute
                  path="/settings/personal-information"
                  exact
                  component={PersonalInformation}
                />
                <PatientRoute
                  path="/settings/contact-information"
                  exact
                  component={ContactInformation}
                />
                <PatientRoute
                  path="/settings/consent"
                  exact
                  component={Consent}
                />
                <PatientRoute
                  path="/complete"
                  exact
                  component={PatientResults}
                />
                <PatientRoute
                  exact
                  path="/complete/take-action"
                  component={TakeAction}
                />
                <PatientRoute path="/education" exact component={Education} />
                <PatientRoute
                  path="/reports"
                  exact
                  component={RiskReportsList}
                />
                <PatientRoute
                  path="/reports/:reportId"
                  exact
                  component={RiskReport}
                />
                <Redirect to="/" />
              </Switch>
            </>
          </PreferredLanguageProvider>
        </Switch>
        <LocationProvider>
          <Pendo />
        </LocationProvider>
        <Zendesk />
      </>
    </AdminContentProvider>
  </PatientAuthProvider>
);

export const Router = () => {
  return (
    <TXProvider>
      <RootLayout>
        <Suspense fallback={<Loading />}>
          <Switch>
            <Route path="/institution" component={KioskRoutes} />
            <Route path="/" component={PatientRoutes} />
            <Redirect to={preserveQueryParams("/")} />
          </Switch>
        </Suspense>
      </RootLayout>
    </TXProvider>
  );
};
